import {
  createRootRouteWithContext,
  Outlet,
  useNavigate,
  useLocation,
  useRouterState,
} from "@tanstack/react-router";
import React from "react";
// eslint-disable-next-line
import { TanStackRouterDevtools } from "@tanstack/router-devtools";
import { IAuthContext, useAuth } from "../contexts/AuthContext";
import { Navbar } from "@/components/layout/Navbar";
import { shouldIntroduce } from "@/components/onboarding/introCards";
import {
  NO_ACCESS_PATH,
  finishLanding,
  isNotAnErrand,
  readGateSearch,
  isPublicPath,
  normalisePath,
} from "@/utils/errandPath";
import { ImpersonationBanner } from "@/components/layout/ImpersonationBanner";
import { MaintenanceBanner } from "@/components/layout/MaintenanceBanner";
import { StaleSessionBanner } from "@/components/layout/StaleSessionBanner";
import { useLayout } from "@/contexts/LayoutContext";
import { useBusinessUnitLanguage } from "@/hooks/useBusinessUnitLanguage";
import { landingPathFor } from "@/utils/roleLanding";

export const Route = createRootRouteWithContext<{ auth: IAuthContext }>()({
  validateSearch: (
    search: Record<string, unknown>,
  ): { redirect?: string; replay?: true } => ({
    redirect: typeof search.redirect === "string" ? search.redirect : undefined,
    // The account menu's way back into the walkthrough (NAV-26).
    //
    // **A boolean, because the router's own encoding is what has to survive
    // the round trip.** Whatever this returns is serialised back into the
    // address, and a *string* value is JSON-quoted on the way out
    // (`?replay=%221%22`) and does not come back equal to what was sent — so
    // the validator dropped the flag it had just written and the gate bounced
    // the reader out of the walkthrough they had asked for. `true` is encoded
    // and parsed as itself. Anything else is absent, so an edited address gets
    // the ordinary behaviour.
    replay: search.replay === true ? true : undefined,
  }),
  component: RootComponent, // eslint-disable-line  @typescript-eslint/no-use-before-define
});

function RootComponent() {
  const {
    user,
    isLoggedIn,
    loading,
    role,
    roleMissing,
    onboardingCompleted,
    profileResolved,
    profileReadFailed,
    isImpersonating,
  } = useAuth();
  /**
   * The errand this account arrived with, kept for the length of the detour.
   *
   * **Because one click used to rewrite it.** The navigation bar renders on
   * `/onboarding` — and has to, since it holds the account menu and therefore
   * the only way to sign out — but a `Link` carries no `?redirect=`, so the
   * gate bounced an unfinished reader back and recomputed the errand from
   * whatever they had just clicked. A new colleague who followed a link to a
   * request and pressed "My Tools" out of curiosity finished on My Tools, with
   * the request they were sent gone and no trace of it.
   *
   * Only a `?redirect=` is latched, never a path synthesised from where
   * somebody is standing: the synthesised one is how a cold load with a
   * persisted session keeps its deep link, and treating it as the errand on
   * every later pass is precisely what let a click become one. First value per
   * account wins, and it is released once the walkthrough is finished.
   */
  const carriedErrand = React.useRef<{ uid: string; errand: string } | null>(
    null,
  );
  /**
   * The account this gate has already sent into the walkthrough.
   *
   * **What tells a deep link from a click.** The errand is either declared in
   * the address or synthesised from the page somebody is standing on, and the
   * synthesised one is legitimate exactly once: on the bounce that interrupts
   * the page they arrived at. Every later bounce is a reader who clicked away
   * from the walkthrough and is being returned to it, where the page they are
   * standing on is the one they just clicked. Both arrive here as
   * `search.redirect` a render later, which is why the latch alone could not
   * tell them apart and a first-value-wins rule latched the click.
   */
  const forcedWalkthrough = React.useRef<string | null>(null);
  const navigate = useNavigate({ from: Route.fullPath });
  const location = useLocation();
  const routerState = useRouterState();
  // **The router's own snapshot, and checked here rather than cast.** The gate
  // branches on the path and the search together, and those were two
  // subscriptions: on a client-side navigation there is a render where the path
  // has moved and the search has not. That render was enough to end a replay
  // before it started — the gate saw `/onboarding` without the flag that says
  // why the reader is there, sent them back, and the address was restored
  // before anything could be observed. Typing the same URL always worked,
  // because a fresh load has no earlier value to lag behind.
  //
  // `location.search` is the *raw* parsed search, so the route's own
  // `validateSearch` has not run over it: `?redirect=5` arrives as the number
  // 5, and asserting a type here instead of testing for one handed a number to
  // `errandIsWorthKeeping`, which honoured it and navigated to `/5`. The two
  // values this gate needs are narrowed the same way the route declares them.
  const { redirect, replay: replayRequested } = readGateSearch(
    routerState.location.search,
  );
  // A reader who has never touched the EN/DE switch starts in their own
  // business unit's language (PRD H7). Here rather than on any page, because
  // the unit's choice has to hold wherever they land first.
  useBusinessUnitLanguage();

  // Where the session was headed before it was interrupted, or the dashboard.
  // `redirect` is a full href captured by the route guards; navigate
  // treats `to` as a bare path, so it has to be parsed — otherwise a deep
  // link's query string and hash are silently dropped on restore. Shared by
  // the three places a session resumes: after signing in, after a role finally
  // reaches an account that was sitting on /no-access, and after a new person
  // finishes or skips the walkthrough.
  const restoreOrHome = React.useCallback(
    (fallback = "/home", useErrand = true) => {
      // **The parse can throw, and it would throw inside a router effect.**
      // `validateSearch` only asks whether `redirect` is a *string*, so
      // `?redirect=http://[not-a-url` reaches here and `new URL` raises. All
      // three readers of that value now agree on it: this one falls back, and
      // `errandGoesSomewhereSpecific` and `errandIsWorthKeeping` both refuse
      // it — so the closing line names the fallback the reader actually gets
      // rather than promising a link nothing can navigate to.
      let url: URL;
      try {
        url = new URL(
          (useErrand ? redirect : undefined) || fallback,
          window.location.origin,
        );
      } catch {
        url = new URL(fallback, window.location.origin);
      }
      navigate({
        to: url.pathname,
        search: Object.fromEntries(url.searchParams),
        hash: url.hash ? url.hash.slice(1) : undefined,
        replace: true,
      });
    },
    [navigate, redirect],
  );

  // **Normalised once, and every path decision in this component reads it.**
  // Fixing only `isNotAnErrand` left the gate's other four comparisons on the
  // raw string, and `onPublicPath` was the one that bit: on `/login/` a
  // signed-in, onboarded reader matched no branch at all — not role-less, not
  // `/no-access`, not `/onboarding`, and not public, because the raw string
  // carries the slash — so `restoreOrHome()` never ran and they sat on the
  // sign-in page under a working menu. That is the NAV-101 dead end this file
  // was written to close. The layout decisions below are the milder half of the
  // same thing: `/home/` would lose its full-width shell, `/no-access/` would
  // render the navbar that page exists to suppress.
  const path = normalisePath(location.pathname);

  React.useEffect(() => {
    if (loading) return;

    const onPublicPath = isPublicPath(path);
    // What this session is actually trying to reach, however it got here.
    //
    // `?redirect=` exists only when the signed-out branch below wrote it, so it
    // covers exactly one journey: follow a link, get bounced to /login, sign
    // in. The ordinary repeat visit does not take it — a persisted Firebase
    // session means `isLoggedIn` is already true when this first runs, there is
    // no /login round trip, and the errand is the URL in the address bar. That
    // is the journey a new colleague makes when they abandon the walkthrough
    // and click the emailed link again the next day, and reading only
    // a dropped `redirect` put them on the dashboard with no trace of it.
    // Latched before it is used, and only from the address: see the ref's own
    // note. First value per account wins, so a later bounce cannot overwrite
    // the deep link somebody actually arrived with.
    const uid = user?.uid;
    if (uid && redirect && !onboardingCompleted && carriedErrand.current?.uid !== uid) {
      carriedErrand.current = { uid, errand: redirect };
    }
    if (onboardingCompleted && carriedErrand.current?.uid === uid) {
      carriedErrand.current = null;
    }
    if (onboardingCompleted && forcedWalkthrough.current === uid) {
      forcedWalkthrough.current = null;
    }

    const carried = carriedErrand.current;
    const latched =
      carried !== null && carried.uid === uid ? carried.errand : undefined;

    const errand =
      redirect ??
      (isNotAnErrand(path)
        ? undefined
        : routerState.location.href);

    if (!isLoggedIn) {
      if (!onPublicPath) {
        navigate({
          to: "/login",
          replace: true,
          search: {
            // `errand`, not a second copy of the same test. **Not the only
            // writer of `?redirect=` — `requireRole` is the other, and for
            // anything under an area route it is the one that fires**, because
            // `beforeLoad` runs before this effect. It captures every href it
            // bounces without asking whether the page is worth remembering,
            // which is why the walkthrough is where a landing is dropped.
            //
            // This branch still matters for the paths no area guard covers:
            // `/`, `/home`, `/onboarding`, `/no-access`. Recomputing from the
            // pathname alone drops the `redirect` half, and a session
            // expiring on `/onboarding?redirect=…` comes back through here
            // with an errand an earlier guard already captured.
            redirect: errand,
          },
        });
      }
      return;
    }

    // Signed in with no role, and none is coming (NAV-101). Checked before
    // every other signed-in branch, because each of them assumes a role: the
    // onboarding gate below asks whether it is `end_user`, and the
    // public-path restore below waits on a role that in this state never
    // arrives — which is what held a role-less account on the sign-in page
    // under a working menu, for ever.
    //
    // `redirect` travels with them. Somebody who followed a deep link, signed
    // in, and turned out to have no role has an errand the app still knows
    // about; dropping it here means that once they are given a role they land
    // on the dashboard instead of where they were going.
    if (roleMissing) {
      if (path !== NO_ACCESS_PATH) {
        navigate({
          to: NO_ACCESS_PATH,
          replace: true,
          search: { redirect: errand },
        });
      }
      return;
    }
    // A role arrived (or the account was given one while it sat here), so the
    // page no longer describes this session.
    //
    // `landingPathFor` and not merely `role`: a role with nowhere to land
    // belongs on this page, and sending it onward would be a redirect loop —
    // /home would send it straight back here, for ever.
    if (path === NO_ACCESS_PATH) {
      if (role && landingPathFor(role)) restoreOrHome();
      return;
    }

    // Leaving the walkthrough resumes the errand rather than always landing on
    // the dashboard. Onboarding sits in front of everything, so somebody who
    // followed a link to a request or an evaluation step, signed in, and turned
    // out to be new reaches it only if `redirect` survives the detour (NAV-26).
    if (path === "/onboarding") {
      // **`replay` is what lets somebody back in.** Finishing the walkthrough
      // is the one thing that cannot be undone by pressing Skip again: the
      // reader who skipped on their first morning had no way to change their
      // mind, and the reader most likely to skip is the one with least time
      // that morning. So the gate stands aside while the account menu's errand
      // is in the address, and the page hands control back by dropping it.
      if (onboardingCompleted && !replayRequested) {
        // An errand wins; otherwise a new member lands in the catalogue rather
        // than on a dashboard. The complaint the walkthrough exists to answer
        // is "no idea what to do first", and the dashboard does not answer it —
        // the thing they came to Navigator to do is find a tool. Only for an
        // end user: an admin who reached this page has their own dashboard.
        //
        // **A landing is not an errand, and this is the only place that may
        // decide so.** `requireRole` captures every href it bounces, because
        // signed out it cannot know whether the reader is a new joiner or an
        // admin returning to a bookmark — and three other consumers need the
        // exact destination back. What must not be overruled is *this* landing
        // choice, so a captured dashboard or area front door is dropped here,
        // where being on `/onboarding` is what says the reader is new.
        // One answer for where this ends, shared with the line that promised
        // it: the fallback used to be chosen here and the promise chosen on the
        // page, and admitting admins made the two disagree.
        const landing = finishLanding(redirect, role);
        restoreOrHome(landing.fallback, landing.honourErrand);
      }
      return;
    }

    // Until the profile doc's first snapshot arrives, onboardingCompleted is
    // the `?? false` fallback — forcing /onboarding on it would hijack the
    // navigation (e.g. the post-login redirect restore) for onboarded users.
    // Never while impersonating: `role` and `onboardingCompleted` are both the
    // target's now, so an admin viewing a user who has not onboarded would be
    // marched into /onboarding — where useCompleteOnboarding writes
    // users/{admin.uid} and would flip the *admin's* own onboarding flags.
    //
    // **Every role with an introduction, and it used to be end users only.**
    // That condition came in with the port and was never examined, so a new BU
    // admin was not given the colleague's walkthrough: they were given nothing
    // at all, on the morning they were handed a queue of other people's
    // requests. The middle screen is chosen by role now (NAV-158), so the
    // question is whether this reader has screens rather than whether they are
    // an employee. The four roles no area admits are left alone: they have no
    // screens to be shown, and nothing assigns them.
    if (
      shouldIntroduce({
        profileResolved,
        profileReadFailed,
        onboardingCompleted,
        role,
        isImpersonating,
      })
    ) {
      // **The synthesised errand counts on the first bounce only.** The
      // latched one wins where there is one; a declared `?redirect=` is always
      // honoured; and the page somebody is standing on becomes the errand only
      // on the bounce that interrupted their arrival. After that a click on
      // the navigation bar is a click, and a reader who arrived with no deep
      // link finishes where the walkthrough sends them instead of on the page
      // they pressed out of curiosity.
      const firstForce = forcedWalkthrough.current !== uid;
      forcedWalkthrough.current = uid ?? null;
      const carriedNow = latched ?? (firstForce ? errand : redirect);
      // Latched here rather than from the address a render later, which is the
      // only point at which the two kinds of errand are still distinguishable.
      if (uid && carriedNow && carriedErrand.current?.uid !== uid) {
        carriedErrand.current = { uid, errand: carriedNow };
      }
      navigate({
        to: "/onboarding",
        replace: true,
        // The errand travels into the walkthrough, the same way it travels to
        // /no-access above. Without it the detour is where a deep link dies.
        search: { redirect: carriedNow },
      });
      return;
    }

    if (onPublicPath) {
      // isLoggedIn flips as soon as the Firebase user exists, but the claims
      // (role) resolve moments later. Restoring before role is known would
      // send the user into a route guard that still sees role=null.
      //
      // `profileResolved` for the same reason one branch down, and it has to be
      // the *same* signal the onboarding gate above waits on: the claims and
      // the first profile snapshot do not arrive together, so on an in-page
      // sign-in there is a commit with `role` set and the profile still
      // pending. Restoring there consumes `?redirect=` and lands the reader on
      // the errand — after which the onboarding gate fires from a page that no
      // longer carries it, and a new colleague finishes the walkthrough at the
      // dashboard instead of the request they were sent. Gating both on
      // `profileResolved` makes that ordering unrepresentable rather than
      // unlikely.
      //
      // Waiting cannot strand anybody: a failed profile read sets
      // `profileResolved` too (with `profileReadFailed` beside it), so this
      // resolves on the error path as well as the happy one.
      if (!role || !profileResolved) {
        return;
      }
      restoreOrHome();
    }
  }, [
    restoreOrHome,
    isLoggedIn,
    loading,
    onboardingCompleted,
    profileResolved,
    role,
    roleMissing,
    isImpersonating,
    path,
    navigate,
    routerState.location.href,
    redirect,
    // Dropping it is what ends a replay, so the gate has to re-run on it.
    replayRequested,
    // The errand latch is keyed by account, so the effect has to see the
    // account change. Every path that changes `user` changes `isLoggedIn` or
    // `role` in the same render, and the `carried.uid === uid` re-check makes a
    // stale closure yield no errand rather than another account's, so this is
    // listed to keep the read and the dependency honest.
    user?.uid,
  ]);

  const isFullWidthRoute = path === "/home";
  // Admin areas render their own shell (routes/{admin,bu-admin,group-legal}
  // via AdminLayout: role sidebar + top bar), so the end-user top navbar is
  // suppressed there and they own their full-height layout.
  const isAdminArea =
    path.startsWith("/admin") ||
    path.startsWith("/bu-admin") ||
    path.startsWith("/group-legal");
  // The end-user layout (routes/end-user/route.tsx) owns its own max-width,
  // padding and mesh background — wrapping it here again would inset the
  // background with gutters. Admin areas are likewise self-contained.
  const isSelfContainedRoute =
    path.startsWith("/end-user") ||
    isAdminArea ||
    path === NO_ACCESS_PATH;
  // The no-role page carries its own sign-out and owns the whole viewport.
  // The navbar is suppressed on it deliberately: its menu is the "working
  // menu" of NAV-101, whose every entry bounces a role-less visitor back to
  // /home, and offering it here would rebuild the dead end the page replaces.
  const isNoAccess = path === NO_ACCESS_PATH;
  const { navbarHeightPx, bannerStackHeightPx, setBannerStackHeightPx } =
    useLayout();
  const bannersRef = React.useRef<HTMLDivElement>(null);

  // **The banners are pinned on every page, and their height is published**
  // (Jira NAV-222, Jira NAV-227). In an admin area the rail is pinned at the
  // height the window leaves it, with "User view" at its foot, so the rail, the
  // top bar and the request summary bar all sit below the banners rather than
  // behind or past them. Elsewhere the fixed navbar sits below them, so a
  // banner never scrolls off and leaves a banner-high strip above the navbar
  // for the page to show through. The window scrolls rather than an inner
  // column, which is what keeps PageDown and the space bar working on a freshly
  // opened page.
  React.useLayoutEffect(() => {
    const element = bannersRef.current;
    if (!element || typeof ResizeObserver === "undefined") {
      setBannerStackHeightPx(0);
      return;
    }
    const update = () => setBannerStackHeightPx(element.offsetHeight);
    update();
    const observer = new ResizeObserver(update);
    observer.observe(element);
    return () => {
      observer.disconnect();
      setBannerStackHeightPx(0);
    };
  }, [setBannerStackHeightPx]);

  // Keyboard focus and in-page scrolling stop below the pinned banners and
  // navbar rather than behind them (WCAG 2.4.11, focus not obscured). An admin
  // area sets its own, below its top bar (AdminLayout).
  React.useEffect(() => {
    if (isAdminArea) return;
    const root = document.documentElement;
    root.style.scrollPaddingTop = `${bannerStackHeightPx + navbarHeightPx}px`;
    return () => {
      root.style.scrollPaddingTop = "";
    };
  }, [isAdminArea, bannerStackHeightPx, navbarHeightPx]);

  return (
    <div className="bg-background min-h-screen">
      {/* z-40: above the page it pins over, below the z-50 dialogs and menus,
          so a dialog dims it and a tall one is never cut off by it. */}
      <div ref={bannersRef} className="sticky top-0 z-40">
        <ImpersonationBanner />
        {isLoggedIn && <StaleSessionBanner />}
        {isLoggedIn && <MaintenanceBanner />}
      </div>
      {!isAdminArea && !isNoAccess && <Navbar />}
      {/* The navbar is fixed, so offset the content by its measured height
          (LayoutContext) to keep page content from underlapping it. */}
      <div
        style={{ paddingTop: navbarHeightPx }}
        className={
          isSelfContainedRoute
            ? undefined
            : isFullWidthRoute
              ? "max-w-[100vw] mx-auto px-[1.5rem]"
              : "max-w-[100rem] mx-auto px-[1.5rem]"
        }
      >
        <Outlet />
      </div>
      {import.meta.env.VITE_ENVIRONMENT === "local" && !navigator.webdriver && (
        <TanStackRouterDevtools />
      )}
    </div>
  );
}
